import React from 'react';
import type { IllustrationKind } from '../../types';

interface IllustrationPalette {
  bg?: string;
  body?: string;
  accent?: string;
}

const DEFAULTS = {
  bg: '#1f3c5f',
  body: '#0f2a4a',
  accent: '#c25a3a',
} as const;

interface HighlightIllustrationProps {
  kind: IllustrationKind;
  palette?: IllustrationPalette;
  ariaLabel?: string;
}

const WalkingPair: React.FC<{ p: Required<IllustrationPalette> }> = ({ p }) => (
  <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" width="100%" height="100%">
    <rect width="100" height="100" fill={p.bg} />
    <rect y="68" width="100" height="32" fill={p.accent} opacity=".6" />
    <g fill={p.body}>
      <circle cx="32" cy="32" r="6" />
      <path d="M26 40 L38 40 L40 75 L36 75 L35 58 L33 75 L29 75 Z" />
    </g>
    <path d="M40 50 Q 56 55, 64 70" stroke="#f3ead8" strokeWidth="1" fill="none" opacity=".85" />
    <g fill={p.body}>
      <ellipse cx="74" cy="80" rx="14" ry="6" />
      <circle cx="84" cy="74" r="5" />
      <path d="M87 70 L91 66 L90 72 Z" />
      <path d="M66 84 L64 88 L67 88 Z" />
      <path d="M80 84 L78 88 L81 88 Z" />
    </g>
  </svg>
);

const Kittens: React.FC<{ p: Required<IllustrationPalette> }> = ({ p }) => (
  <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" width="100%" height="100%">
    <rect width="100" height="100" fill={p.bg} />
    <g fill={p.body}>
      <ellipse cx="36" cy="62" rx="18" ry="20" />
      <path d="M22 46 L18 32 L32 42 Z" />
      <path d="M50 46 L54 32 L40 42 Z" />
      <ellipse cx="68" cy="68" rx="16" ry="18" />
      <path d="M56 54 L52 42 L64 50 Z" />
      <path d="M80 54 L84 42 L72 50 Z" />
    </g>
    <g fill="#f3ead8">
      <ellipse cx="30" cy="60" rx="2" ry="2.5" />
      <ellipse cx="42" cy="60" rx="2" ry="2.5" />
      <ellipse cx="62" cy="66" rx="2" ry="2.5" />
      <ellipse cx="74" cy="66" rx="2" ry="2.5" />
    </g>
  </svg>
);

const PetPortrait: React.FC<{ p: Required<IllustrationPalette> }> = ({ p }) => (
  <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" width="100%" height="100%">
    <rect width="100" height="100" fill={p.bg} />
    <g fill={p.body}>
      <ellipse cx="50" cy="58" rx="26" ry="28" />
      <ellipse cx="30" cy="38" rx="9" ry="14" transform="rotate(-18 30 38)" />
      <ellipse cx="70" cy="38" rx="9" ry="14" transform="rotate(18 70 38)" />
    </g>
    <g fill="#f3ead8">
      <circle cx="42" cy="56" r="2.5" />
      <circle cx="58" cy="56" r="2.5" />
      <ellipse cx="50" cy="68" rx="3.5" ry="2.5" />
    </g>
    <path d="M45 76 Q50 80 55 76" stroke="#f3ead8" strokeWidth="1.4" fill="none" />
  </svg>
);

const CatPortrait: React.FC<{ p: Required<IllustrationPalette> }> = ({ p }) => (
  <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" width="100%" height="100%">
    <rect width="100" height="100" fill={p.bg} />
    <g fill="#f3ead8" opacity={0.9}>
      <ellipse cx="50" cy="60" rx="24" ry="22" />
      <path d="M30 44 L25 22 L43 38 Z" />
      <path d="M70 44 L75 22 L57 38 Z" />
      <ellipse cx="40" cy="54" rx="3" ry="5" />
      <ellipse cx="60" cy="54" rx="3" ry="5" />
      <path d="M47 63 L53 63 L50 67 Z" />
    </g>
    <g stroke="#f3ead8" strokeWidth="0.7" fill="none" opacity={0.4}>
      <line x1="32" y1="64" x2="20" y2="62" />
      <line x1="32" y1="68" x2="20" y2="69" />
      <line x1="68" y1="64" x2="80" y2="62" />
      <line x1="68" y1="68" x2="80" y2="69" />
    </g>
  </svg>
);

const Paws: React.FC<{ p: Required<IllustrationPalette> }> = ({ p }) => (
  <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" width="100%" height="100%">
    <rect width="100" height="100" fill={p.bg} />
    <g fill={p.body}>
      <ellipse cx="32" cy="40" rx="7" ry="9" />
      <circle cx="22" cy="30" r="3" />
      <circle cx="30" cy="24" r="3" />
      <circle cx="40" cy="28" r="3" />
      <circle cx="44" cy="38" r="3" />
      <ellipse cx="68" cy="72" rx="7" ry="9" />
      <circle cx="58" cy="62" r="3" />
      <circle cx="66" cy="56" r="3" />
      <circle cx="76" cy="60" r="3" />
      <circle cx="80" cy="70" r="3" />
    </g>
  </svg>
);

/** A recovering patient: dog in a vet cone, with a little bandaged paw. */
const Cone: React.FC<{ p: Required<IllustrationPalette> }> = ({ p }) => (
  <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" width="100%" height="100%">
    <rect width="100" height="100" fill={p.bg} />
    <rect y="78" width="100" height="22" fill={p.accent} opacity=".55" />
    <g fill={p.body}>
      <ellipse cx="50" cy="76" rx="22" ry="12" />
      <ellipse cx="50" cy="52" rx="15" ry="14" />
      <ellipse cx="37" cy="44" rx="5" ry="9" transform="rotate(-20 37 44)" />
      <ellipse cx="63" cy="44" rx="5" ry="9" transform="rotate(20 63 44)" />
      <rect x="38" y="80" width="8" height="12" rx="3" />
    </g>
    <path d="M24 30 L76 30 L64 66 L36 66 Z" fill="#f3ead8" opacity=".22" stroke="#f3ead8" strokeWidth="1.2" strokeOpacity=".8" />
    <g fill="#f3ead8">
      <circle cx="44" cy="52" r="2.2" />
      <circle cx="56" cy="52" r="2.2" />
      <ellipse cx="50" cy="59" rx="3" ry="2.2" />
    </g>
    <rect x="53" y="82" width="10" height="9" rx="2" fill="#f3ead8" />
    <path d="M54 85 H62 M54 88 H62" stroke={p.accent} strokeWidth=".8" />
    <path d="M78 22 C 78 18, 84 18, 84 22 C 84 18, 90 18, 90 22 C 90 27, 84 30, 84 32 C 84 30, 78 27, 78 22 Z" fill={p.accent} />
  </svg>
);

const HighlightIllustration: React.FC<HighlightIllustrationProps> = ({
  kind,
  palette,
  ariaLabel,
}) => {
  const p: Required<IllustrationPalette> = { ...DEFAULTS, ...palette };
  let body: React.ReactNode;
  switch (kind) {
    case 'kittens':
      body = <Kittens p={p} />;
      break;
    case 'pet-portrait':
      body = <PetPortrait p={p} />;
      break;
    case 'cat-portrait':
      body = <CatPortrait p={p} />;
      break;
    case 'paws':
      body = <Paws p={p} />;
      break;
    case 'cone':
      body = <Cone p={p} />;
      break;
    case 'walking-pair':
    default:
      body = <WalkingPair p={p} />;
  }

  return (
    <div
      role="img"
      aria-label={ariaLabel ?? kind}
      style={{ width: '100%', height: '100%' }}
    >
      {body}
    </div>
  );
};

export default HighlightIllustration;
